import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useGraphFrame } from './useGraphFrame'
import { onAfterDraw } from './frameBus'
import { canvasTookTouch, handToCanvas } from './labelTouch'
import { growth } from './growth'
import { screen } from './screen'
import { fitCamera, useStore } from '../core/store'
import { channelOf, type Channel, type ReasonNode } from '../core/types'
import { PRIOR_VERSION_CAPTION, priorVersionIds } from '../core/rebase'
import { drawnRatingsOf } from '../core/rewrite'
import { SeedLabel, type Register } from './SeedLabel'
import { seedCaptionBox, seedScale } from './seedCaption'
import { NodeTree, treeFocus } from './NodeTree'
import { OBJECTION_STOOD_BELOW, objectionsStoodUnder, stoodAgainst } from '../core/endings'

/** The status table's clause, opening a line, as the card opens it. */
const stoodClause = OBJECTION_STOOD_BELOW.charAt(0).toUpperCase() + OBJECTION_STOOD_BELOW.slice(1)

/**
 * Text lives in the DOM, not on the canvas.
 *
 * Canvas text is blurry at fractional device pixels, ignores the user's font
 * settings, and can't be selected or read by a screen reader. The cost is that
 * positions must be pushed to the DOM every frame — so this component writes
 * `style.transform` directly and never re-renders during a pan.
 *
 * Decluttering is a coarse occupancy grid in screen space, walked in priority
 * order. Cheap, stable, and it drops the least important label rather than the
 * unluckiest one.
 */

const CELL_W = 46
const CELL_H = 11

/**
 * The nearest a caption's centre is set to its own node's, px: the placement
 * list's first spot on either side of an unfolded node's spoke, where a
 * single-line caption clears the glyph's own radius. Nearer than this, the
 * caption is drawn on the dot it names.
 */
const GLYPH_CLEAR = 20

/**
 * Height of the floating furniture along the bottom of the stage.
 *
 * The legend, transport and minimap sit at `--z-float` above the caption layer,
 * and the occupancy grid cannot see them — so a caption placed in that band is
 * simply covered by a panel. Reserving it is cheaper and more honest than
 * letting labels disappear under chrome.
 */
const POD_H = 50

/**
 * Breathing room around the session column's rectangle, top-left.
 *
 * MEASURED rather than declared. That column holds the run pod and that move's
 * thinking, and it moves sideways with the explorations sidebar — every part of
 * which changes size with its content. A hardcoded number is either too small
 * (captions render underneath an opaque panel) or too large (a band of good
 * captions exiled for nothing), and it silently becomes wrong the first time any
 * of them gains a line. The layout is radial from the world origin, so this
 * quadrant carries real captions and getting it wrong is visible.
 *
 * One rect pair per placement pass, unconditionally — the column is always on
 * screen, so the corner is always taken. The same order of cost as the four
 * footprint measurements this file already takes per pass.
 */
const RUN_PAD = 8

/**
 * Breathing room around the measured furniture — the minimap, the graph's
 * footer (D369), the tool dock. Each is MEASURED like the run slot rather than assumed at a
 * corner: they move with the floating panes' insets, render conditionally, and
 * on a narrow stage some do not render at all — all facts a set of corner
 * constants cannot know. No element, no reserve.
 */
const MAP_PAD = 8

/**
 * How often the placement pass runs when nothing it draws has moved.
 *
 * The pass used to run after every canvas frame, unconditionally: eight
 * `querySelector`s, nine rectangle reads, a sort and a style write per caption,
 * sixty times a second, for a picture identical to the last one. The renderer
 * now says on `screen` whether a position could have changed this frame; when
 * it could not, the pass still runs at this cadence — ten times a second — so
 * a panel opening or a pod arriving, which the renderer cannot see, moves the
 * captions out of its way within a tenth of a second rather than the very next
 * frame. See docs/performance-audit.md, finding 6.
 *
 * A time and not a frame count, because the frames stopped coming at one rate.
 * It was every sixth frame, which was a tenth of a second only while the canvas
 * drew sixty a second; at the loop's resting rate (`cadence.ts`) it would have
 * been three tenths, and under reduced motion never.
 */
const IDLE_MS = 100

/**
 * One integer per occupancy cell. The offsets keep the key positive for cells
 * left of or above the stage, where a candidate can land before `inside()`
 * rejects it — and an integer key is what stops the grid from allocating a
 * string per cell per caption per pass.
 */
const cellKey = (cx: number, cy: number) => (cx + 4096) * 8192 + (cy + 4096)

function priority(
  n: ReasonNode,
  selected: string | null,
  hover: string | null,
  guideTarget: string | null,
  fold: number,
): number {
  if (n.parentId === null) return 1000
  if (n.id === selected) return 900
  if (n.id === hover) return 890
  // The guide must be able to name the thing it is pointing at, but it does not
  // outrank a node the person is already holding or hovering.
  if (n.id === guideTarget) return 880
  const ch = channelOf(n)
  let p = 100 - n.depth * 9
  // The renderer's own reading of the numbers, so a caption does not outrank its
  // neighbours on ratings the disc under it no longer draws (`drawnRatingsOf`).
  const rated = drawnRatingsOf(n)
  p += rated.novelty * 30
  p += rated.confidence * 12
  if (ch === 'insight') p += 34
  if (ch === 'tension') p += 16
  if (ch === 'gap') p += 10
  if (ch === 'dead') p -= 30
  if (n.editedAt) p += 20
  // A stub stands for a whole branch, so it should survive the zoom gate
  // further out than the single node it is drawn as.
  if (fold > 0) p += 12 + Math.min(24, Math.log2(1 + fold) * 8)
  return p
}

/**
 * The two-tier tone a caption wears, one object per channel for the life of
 * the page. A fresh object per render defeated React's style diff on every
 * caption every time the list rendered (finding 7).
 *
 * Two tiers of the same meaning: `--tone` is the mark colour (the selection
 * rule), `--tone-ink` is the reading colour (the glyph itself).
 */
const toneStyles = new Map<Channel, CSSProperties>()
function toneStyle(ch: Channel): CSSProperties {
  let s = toneStyles.get(ch)
  if (!s) {
    s = { '--tone': `var(--sem-${ch})`, '--tone-ink': `var(--ink-${ch})` } as CSSProperties
    toneStyles.set(ch, s)
  }
  return s
}

/**
 * One caption. Memoised on its own inputs, so a revision that touched one node
 * reconciles one button rather than the whole list — the list re-rendering as
 * one array cost a few milliseconds per beat on a large graph (finding 7).
 * The frame loop owns `transform`, `scale`, `opacity` and `visibility` on the
 * element; nothing here writes those.
 *
 * Every caption but the seed's. The seed's text is the whole prompt, clamped,
 * with a door to the rest, which is two controls and so a different element —
 * `SeedLabel.tsx`. The list below picks by `parentId`.
 */
const Label = memo(function Label({
  n,
  fold,
  selected,
  guide,
  stood,
  register,
}: {
  n: ReasonNode
  fold: number
  selected: boolean
  guide: boolean
  /** The objections that stood below this claim, named — what its notch means,
   *  said on the caption's hover in the card's words — or undefined. */
  stood?: string
  register: Register
}) {
  const ch = channelOf(n)
  const toned = ch !== 'signal'
  return (
    <button
      ref={(el) => register(n.id, el)}
      className="nlabel"
      tabIndex={-1}
      aria-hidden
      style={toned ? toneStyle(ch) : undefined}
      data-tone={toned ? ch : undefined}
      data-dead={ch === 'dead' || undefined}
      data-sel={selected || undefined}
      data-guide={guide || undefined}
      onClick={(e) => {
        e.stopPropagation()
        // A finger's tap here was the canvas's to answer (`labelTouch.ts`).
        if (canvasTookTouch(e.nativeEvent)) return
        if (e.altKey) {
          useStore.getState().toggleCollapse(n.id)
          return
        }
        useStore.getState().select(n.id)
        useStore.getState().setRail(true)
      }}
      onDoubleClick={(e) => {
        e.stopPropagation()
        const st = useStore.getState()
        st.setFocus(st.focusId === n.id ? null : n.id)
      }}
      onPointerEnter={() => useStore.getState().hover(n.id)}
      onPointerLeave={() => useStore.getState().hover(null)}
      // A finger here is the canvas's: one finger of a pinch, or a pan begun on
      // the words. Its tap still selects this node (`labelTouch.ts`, D340).
      onPointerDown={(e) => {
        if (handToCanvas(e.nativeEvent, n.id)) e.stopPropagation()
      }}
      title={stood ? `${n.claim}\n\n${stood}` : n.claim}
    >
      {n.label}
      {fold > 0 && <span className="nlabel-fold">{fold}</span>}
    </button>
  )
})

/**
 * The focus ring's diameter round an ordinary node, in pixels. Fixed rather than
 * scaled with the zoom: a dot is 4 to 16 pixels across at any zoom this canvas
 * allows, and a ring that shrank with it would stop being findable at exactly
 * the overview zoom a keyboard user is most likely to be walking the tree at.
 */
const RING_D = 30

/** What the frame loop last wrote to a caption's style. */
interface Written {
  vis: string
  transform: string
  scale: string
  opacity: string
}

export function Labels() {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const nodeRefs = useRef(new Map<string, HTMLElement>())
  const ringRef = useRef<HTMLDivElement | null>(null)
  const { session, rev, visible, folded, childrenOf, guideKeep, guideTargetId } = useGraphFrame()
  const showLabels = useStore((s) => s.filters.labels)

  /** Stable, so a memoised caption's `ref` prop is the same function every render. */
  const register = useCallback<Register>((id, el) => {
    if (el) nodeRefs.current.set(id, el)
    else nodeRefs.current.delete(id)
  }, [])

  const items = useMemo(() => {
    const list: ReasonNode[] = []
    for (const id of session.order) {
      const n = session.nodes[id]
      if (n && visible.has(id)) list.push(n)
    }
    return list
  }, [session, visible, rev])

  /**
   * Cell footprint per label, measured once per set change.
   *
   * Claiming a single cell for a label that is 170px wide and two lines tall
   * is what produces the classic overlapping-label mess: the grid says a spot
   * is free when the neighbour's text is already sitting in it. Measuring is a
   * forced layout, so it happens on change — never in the frame loop.
   */
  const footprints = useRef(new Map<string, [number, number]>())

  /**
   * Estimated box from the text itself, for the frames before a real
   * measurement exists (and for any context where layout reports zero — a
   * backgrounded tab does). Falling back to a single grid cell instead would
   * silently switch collision detection off, which looks exactly like a bug.
   */
  const estimate = (n: ReasonNode): [number, number] => {
    // The seed's box is its title's, from the same function that writes the
    // title, so the two cannot disagree (`seedCaption.ts`).
    if (n.parentId === null) {
      const [w, h] = seedCaptionBox(n.label)
      return [w + 8, h + 10]
    }
    // These mirror `.nlabel` in global.css and must be kept in lockstep with
    // it — `maxW` in particular has to equal the CSS `max-width` exactly, or
    // the estimate wraps at a different column than the browser does.
    const charW = 7.0
    const maxW = 176
    const lineH = 17
    const padX = 12
    const padY = 6
    const fold = folded.get(n.id) ?? 0
    const textW = (n.label.length + (fold ? String(fold).length + 3 : 0)) * charW
    const w = Math.min(maxW, textW + padX)
    // Wrapping happens at the *content* width. `box-sizing: border-box` means
    // max-width includes the padding, so dividing by maxW under-counts lines.
    const lines = Math.max(1, Math.ceil(textW / (maxW - padX)))
    return [w + 8, lines * lineH + padY + 10]
  }

  /**
   * Measure one label, caching only a real result. Returns null if unmeasurable.
   *
   * Hiding is `visibility`, not `display`, so a hidden label still has a box to
   * measure — no need to reveal it and put it back.
   */
  const measure = (id: string, el: HTMLElement): [number, number] | null => {
    const w = el.offsetWidth
    const h = el.offsetHeight
    // A backgrounded tab reports 0 for every box. Caching that would silently
    // disable collision detection until the next structural change — so we
    // decline to cache it and retry on a later frame instead.
    if (w <= 0 || h <= 0) return null
    // The halo bleeds ~5px past the box `offsetWidth` reports, and two captions
    // whose halos overlap read as one smudged phrase. The footprint must never
    // shrink: a smaller box lets more labels through the declutter pass, which
    // is the opposite of what this layer is for.
    const fp: [number, number] = [w + 8, h + 10]
    footprints.current.set(id, fp)
    // The seed's rim is fitted to its INK, not its box: the box is the
    // column's full max-width whenever the title wraps, which can be a third
    // wider than the words, and a rim fitted to it stood far off the text.
    // Read off a range over the title, unscaled by the ratio of the box's
    // layout width to its drawn one — the frame loop scales this box.
    if (el.dataset.root) {
      const title = el.querySelector('.nlabel-seed')
      const drawn = el.getBoundingClientRect().width
      if (title && drawn > 0) {
        const range = document.createRange()
        range.selectNodeContents(title)
        const ink = range.getBoundingClientRect().width * (w / drawn)
        if (ink > 0) seedInk.current = [Math.min(w, ink + 8), h + 4]
      }
    }
    return fp
  }

  /** The seed caption's ink box, unscaled: the widest line of its title, and
   *  the caption's height. Null until measured. */
  const seedInk = useRef<[number, number] | null>(null)

  /** Round-robin cursor for the per-pass footprint refresh. */
  const cursor = useRef(0)
  /** What each caption last measured *as* — text plus its fold count. */
  const sigs = useRef(new Map<string, string>())

  useLayoutEffect(() => {
    // A caption that gains a fold count keeps its old, too-small box otherwise,
    // and a footprint that is too small turns off both the collision test and
    // the on-stage test. This also makes a label renamed by a rewrite
    // re-measure at once instead of waiting for the round robin.
    for (const n of items) {
      const sig = `${n.label}|${folded.get(n.id) ?? 0}`
      if (sigs.current.get(n.id) !== sig) {
        sigs.current.set(n.id, sig)
        footprints.current.delete(n.id)
      }
    }
    // Measure what's new; keep what we already know.
    //
    // Clearing the whole cache on every arrival meant re-measuring 26 labels at
    // the one moment they are least measurable — mid-commit, with a brand-new
    // element still settling — and any bad reading then replaced a good one.
    for (const n of items) {
      if (!footprints.current.has(n.id)) {
        const el = nodeRefs.current.get(n.id)
        if (el) measure(n.id, el)
      }
    }
    if (footprints.current.size > items.length) {
      const live = new Set(items.map((n) => n.id))
      for (const id of [...footprints.current.keys()]) {
        if (!live.has(id)) footprints.current.delete(id)
      }
      for (const id of [...sigs.current.keys()]) {
        if (!live.has(id)) sigs.current.delete(id)
      }
    }
  }, [items, folded])

  useEffect(() => {
    const occupied = new Map<number, number>()
    // Memoised on the node table, so a lookup per caption per pass.
    const prior = priorVersionIds(session)

    /**
     * What was last written to each caption, so a pass that arrives at the
     * same answer writes nothing. A style write is a style invalidation even
     * when the value is unchanged, and at rest every one of them was.
     */
    const written = new WeakMap<HTMLElement, Written>()
    const hide = (el: HTMLElement) => {
      const w = written.get(el)
      if (w) {
        if (w.vis === 'hidden') return
        w.vis = 'hidden'
      } else {
        written.set(el, { vis: 'hidden', transform: '', scale: '', opacity: '' })
      }
      el.style.visibility = 'hidden'
    }
    const show = (el: HTMLElement, transform: string, scale: string, opacity: string) => {
      const w = written.get(el)
      if (!w) {
        el.style.visibility = 'visible'
        el.style.transform = transform
        el.style.scale = scale
        el.style.opacity = opacity
        written.set(el, { vis: 'visible', transform, scale, opacity })
        return
      }
      if (w.vis !== 'visible') {
        w.vis = 'visible'
        el.style.visibility = 'visible'
      }
      if (w.transform !== transform) {
        w.transform = transform
        el.style.transform = transform
      }
      if (w.scale !== scale) {
        w.scale = scale
        el.style.scale = scale
      }
      if (w.opacity !== opacity) {
        w.opacity = opacity
        el.style.opacity = opacity
      }
    }

    /** The frame the last pass placed against, and what it read off the store. */
    let lastFrame = -1
    let lastSel: string | null = null
    let lastHover: string | null = null
    let lastW = -1
    let lastH = -1
    let passAt = -Infinity

    const tick = () => {
      const host = hostRef.current
      if (!host) return
      const s = useStore.getState()
      const { selectedId, hoverId } = s
      const W = host.clientWidth
      const H = host.clientHeight

      // Nothing this pass places has moved since the last one: the renderer
      // bumped no frame, no strand is mid-growth, and neither the pointer nor
      // the selection changed priority. Run at the idle cadence instead — see
      // IDLE_MS for why not never.
      const still =
        screen.frame === lastFrame &&
        !screen.animating &&
        selectedId === lastSel &&
        hoverId === lastHover &&
        W === lastW &&
        H === lastH
      lastFrame = screen.frame
      lastSel = selectedId
      lastHover = hoverId
      lastW = W
      lastH = H
      const at = performance.now()
      if (still && at - passAt < IDLE_MS) return
      passAt = at

      // Wall-clock, to match `birth` and the renderer's growth clock.
      const now = Date.now()
      occupied.clear()

      // The zoom a Fit would choose for this graph on this stage: the seed's
      // caption is its set size there and scales with its ring from it.
      const fitK = screen.mode === '2d' ? fitCamera(s.bounds, W, H, s.viewInset).k : screen.zoom

      // Re-measure a few labels per pass, before anything is written.
      //
      // A footprint that is too *small* is quietly destructive: it turns off
      // both the collision test and the on-stage test, and the symptom is a
      // caption hanging over the edge of the stage or sitting on top of its
      // neighbour. And a footprint can go stale for perfectly ordinary reasons
      // — the display font finishing loading after the measurement, a label
      // rewritten in place, a re-wrap. So the cache heals itself rather than
      // being trusted forever.
      //
      // All the reads happen here, ahead of the placement pass's writes, so
      // this costs one layout flush per pass instead of one per label.
      if (items.length > 0) {
        const n = Math.min(4, items.length)
        for (let i = 0; i < n; i++) {
          const it = items[(cursor.current + i) % items.length]!
          const el = nodeRefs.current.get(it.id)
          if (el) measure(it.id, el)
        }
        cursor.current = (cursor.current + n) % items.length
      }

      // Read per pass, not subscribed: this layer never re-renders React, so a
      // panel opening or a pod arriving has to change the corner on the next
      // pass. MEASURED edges, not an assumed corner: the chrome that holds this
      // has moved with every chrome change so far, so "top-left plus 16" is a
      // guess with a short shelf life.
      const hr = host.getBoundingClientRect()
      // The session column — the run pod and its live trace, in the corner the
      // explorations sidebar leaves them. Measured every pass and NOT gated on
      // a run: the column is always on screen, so this corner is always taken,
      // where the pod's rectangle only existed while a run did. The reserve runs
      // to the stage's own corner, so a caption cannot slide into the gap
      // between the column and the edge — which is also how the sidebar's own
      // column is covered above the pod for free, whatever width it is at.
      const corner = document.querySelector('.slot-session')
      let cornerW = 0
      let cornerH = 0
      if (corner) {
        const sr = (corner as HTMLElement).getBoundingClientRect()
        cornerW = sr.right - hr.left + RUN_PAD
        cornerH = sr.bottom - hr.top + RUN_PAD
      }
      // The minimap, the graph's footer and the tool dock, where they actually are
      // — or nowhere, when they are not drawn. Same measured-rect rule as the
      // session column: floating furniture moves with the insets, so corners
      // cannot be assumed.
      const blocks: Array<{ l: number; t: number; r: number; b: number }> = []
      // `.coachline`, its ✕ and `.coachdigest` join them because they are
      // the same kind of thing: floating chrome that appears and disappears, whose
      // left edge is measured from the session column rather than assumed. They
      // are separate rects rather than their shared column's one, so the gaps
      // between them stay available to captions.
      //
      // The COACH PINS are deliberately NOT here. Every rect in this list is a
      // fixed panel; a pin is anchored to a node and travels with the graph, so
      // reserving it would drag a caption-free band around the field all frame.
      // A pin sits up-right of its node instead (`ui/CoachPins.tsx`) and the
      // residual overlap with that node's own caption is accepted — they name
      // the same node, and both are small. That layer keeps its own copy of
      // these selectors and READS them: a caption under a panel is merely
      // unreadable, where a pin under one is a control nobody can press.
      //
      // The COACH CARD's popup form is absent for the same reason and reads the
      // same way (D25). It is a bigger box than a pin, which makes the argument
      // stronger rather than weaker: it is anchored to a node, it moves with
      // every frame of a camera flight, and it is temporary reading chrome that
      // the next J or Escape takes away — so a reserved rectangle for it would
      // be a caption-free band the size of a panel towed across the field and
      // then abandoned. It clamps ITSELF out of the two fixed boxes it could
      // actually be swallowed by (`ui/Coach.tsx`), which is this list's own
      // trade made from the mover's side.
      //
      // The card's PHONE form is the other case, and joins the list: a row in
      // the run-done column like the line above it, docked rather than anchored,
      // the field's width and half its height. Unreserved, the captions under it
      // read through its glass as if set on it.
      for (const sel of [
        '.minimap',
        // The graph footer (D369): the transport, the scrubber and the view's
        // switches, in one strip along the field's bottom edge.
        '.graphfoot',
        '.tooldock',
        '.coachline-close',
        '.coachline',
        '.coachdigest',
        '.coach--column',
      ]) {
        const el = document.querySelector(sel)
        if (!el) continue
        const mr = (el as HTMLElement).getBoundingClientRect()
        blocks.push({
          l: mr.left - hr.left - MAP_PAD,
          t: mr.top - hr.top - MAP_PAD,
          r: mr.right - hr.left + MAP_PAD,
          b: mr.bottom - hr.top + MAP_PAD,
        })
      }
      // The two panes, by their labels rather than a class: their shell is
      // `GlassPane`, which takes no `className`. One selector finds each pane
      // in BOTH of its shapes — a desktop column and a phone overlay or sheet,
      // which reserve no inset at all — so these are measured rects like the
      // rest and not arithmetic off `viewInset.left`. The thread joined the
      // explorations pane here when it moved beside it: it floats over the
      // field like every other panel, and the caption layer could not see it,
      // so a first-ring caption half under its glass read "agnetic levitation".
      //
      // Measured on their own rather than inside the loop because their EDGES
      // are wanted twice: once as blocks, and once as the bounds of the seed's
      // own caption, which is exempt from the collision pass below and has to
      // dodge a full-height column or a bottom sheet by hand — the same reason
      // the session column is.
      let gutterW = 0
      let floorY = H - POD_H
      for (const sel of ['aside[aria-label="Explorations"]', 'aside[aria-label="Thread"]']) {
        const pane = document.querySelector(sel)
        if (!pane) continue
        const pr = (pane as HTMLElement).getBoundingClientRect()
        if (pr.width <= 0 || pr.height <= 0) continue
        blocks.push({
          l: pr.left - hr.left - MAP_PAD,
          t: pr.top - hr.top - MAP_PAD,
          r: pr.right - hr.left + MAP_PAD,
          b: pr.bottom - hr.top + MAP_PAD,
        })
        // A column hangs from the stage's top edge and holds the left one; a
        // sheet rises from the bottom and holds that. Decided by shape, not by
        // breakpoint, so either pane can take either shape — and by the top
        // edge rather than the height, because the phone's sheet is taller
        // than half the stage and still a sheet.
        if (pr.top - hr.top <= 24) gutterW = Math.max(gutterW, pr.right - hr.left + MAP_PAD)
        else floorY = Math.min(floorY, pr.top - hr.top - MAP_PAD)
      }
      // The floating composer, where it floats — on a blank canvas, a desktop
      // with the thread closed, and a phone. Opaque like the panes, so a caption
      // under it is lost outright. Empty while the composer
      // sits in the thread's column (a zero-height strip), which reserves nothing.
      const strip = document.querySelector('.slot-composer')
      if (strip) {
        const sr = (strip as HTMLElement).getBoundingClientRect()
        if (sr.width > 0 && sr.height > 0) {
          blocks.push({
            l: sr.left - hr.left - MAP_PAD,
            t: sr.top - hr.top - MAP_PAD,
            r: sr.right - hr.left + MAP_PAD,
            b: sr.bottom - hr.top + MAP_PAD,
          })
          floorY = Math.min(floorY, sr.top - hr.top - MAP_PAD)
        }
      }

      // Highest priority first, so the important label wins its cell.
      //
      // In the spatial view depth joins priority, and it has to: two nodes can
      // project to the same pixel from opposite sides of the graph, and the one in
      // front is the one the reader is looking at. Without this the caption of a
      // node hidden behind the graph can win the cell over the node occluding it —
      // a word floating in front of the thing it is not naming. The term is scaled
      // to be a tie-breaker between comparable claims rather than something that
      // outranks a contradiction.
      const ordered = items
        .map((n) => {
          const base = priority(n, selectedId, hoverId, guideTargetId, folded.get(n.id) ?? 0)
          if (screen.mode !== '3d' || n.parentId === null) return { n, p: base }
          const q = screen.place(n.id)
          return { n, p: base + (q.on ? 26 * Math.exp(-q.z / 2600) : 0) }
        })
        .sort((a, b) => b.p - a.p)

      // The seed orb is the one fixed obstacle on the canvas. Reserve its
      // footprint up front so first-ring labels are pushed clear of it rather
      // than silently dropped for colliding with the thing they branch from.
      // `screen.orbR` is the seed's clear field (`ROOT_CLEAR` in Renderer.ts),
      // published by the renderer each frame, so the two cannot drift.
      const ORB = screen.orbR + 26

      for (const { n, p } of ordered) {
        const el = nodeRefs.current.get(n.id)
        if (!el) continue

        if (
          !showLabels &&
          n.parentId !== null &&
          n.id !== selectedId &&
          n.id !== hoverId &&
          n.id !== guideTargetId
        ) {
          hide(el)
          continue
        }

        const isRoot = n.parentId === null

        // Nothing is written until the node it names has landed.
        //
        // A node joins the session the moment the model emits it, but its
        // strand still has to probe and extend before the node exists on
        // screen. A caption shown at emission is a phrase sitting on empty
        // canvas for two seconds — text attached to nothing, which is the one
        // thing this overlay must never produce. It fades up with the
        // touchdown flash instead, so the word arrives with the node.
        const age = now - growth.bornAt(n.id, n.birth)
        // 380 is numerically --dur-slow, but it belongs to the canvas clock
        // rather than the DOM one — it is deliberately a literal here so a
        // stylesheet change can never silently desync words from touchdown.
        const fadeMs = growth.calm ? 140 : 380
        const landed = growth.landsAfter <= 0 ? 1 : (age - growth.landsAfter) / fadeMs
        if (landed <= 0) {
          hide(el)
          continue
        }
        const settle = landed >= 1 ? 1 : landed * landed * (3 - 2 * landed)

        // The rendered position, from whichever camera just painted — never
        // recomputed here. A label that tracked the layout target would detach
        // from its strand while the graph settles, and one that assumed an affine
        // transform would be wrong in the spatial view.
        const q = screen.place(n.id)
        if (!q.on) {
          hide(el)
          continue
        }
        const x = q.x
        const y = q.y

        let lx = x
        let ly = y

        // Gate on the *node*, not on the label. A node outside the stage has
        // nothing on screen for its caption to belong to, and any attempt to
        // bring that caption into view strands it against the edge — a label
        // floating in empty space, pointing at nothing.
        if (W > 0 && H > 0 && (x < -4 || x > W + 4 || y < -4 || y > H + 4)) {
          hide(el)
          continue
        }

        // Below a certain zoom only the load-bearing labels survive.
        const zoomGate =
          isRoot ||
          n.id === selectedId ||
          n.id === hoverId ||
          n.id === guideTargetId ||
          p > 118 - screen.zoom * 90
        if (!zoomGate) {
          hide(el)
          continue
        }

        const [bw, bh] = footprints.current.get(n.id) ?? measure(n.id, el) ?? estimate(n)
        // The seed's caption scales with the camera (`seedScale`); every
        // other caption keeps its type size. Its measured box is unscaled, so
        // the box it occupies on screen is the measure times the scale.
        const ink = isRoot ? (seedInk.current ?? [bw, bh]) : null
        const ss = ink ? seedScale(screen.zoom, fitK, Math.hypot(ink[0] / 2, ink[1] / 2)) : 1
        const pw = bw * ss
        const ph = bh * ss

        /** Is the whole box inside the readable area? Zero size means unmeasurable — allow it. */
        const inside = (px: number, py: number) => {
          if (W < pw + 12 || H < ph + POD_H + 12) return true
          if (
            px - pw / 2 < 2 ||
            px + pw / 2 > W - 2 ||
            py - ph / 2 < 2 ||
            py + ph / 2 > H - POD_H
          ) {
            return false
          }
          // …nor underneath the session column, which floats above this layer too
          // and is there whether or not a run is going.
          if (cornerH > 0 && px - pw / 2 < cornerW && py - ph / 2 < cornerH) return false
          // …and not underneath the measured furniture, which floats above this
          // layer: the minimap, the graph's footer, the tool dock.
          for (const bk of blocks) {
            if (
              px + pw / 2 > bk.l &&
              px - pw / 2 < bk.r &&
              py + ph / 2 > bk.t &&
              py - ph / 2 < bk.b
            )
              return false
          }
          return true
        }

        const free = (px: number, py: number) => {
          const x0 = Math.floor((px - pw / 2) / CELL_W)
          const x1 = Math.floor((px + pw / 2) / CELL_W)
          const y0 = Math.floor((py - ph / 2) / CELL_H)
          const y1 = Math.floor((py + ph / 2) / CELL_H)
          for (let cx = x0; cx <= x1; cx++) {
            for (let cy = y0; cy <= y1; cy++) {
              const held = occupied.get(cellKey(cx, cy))
              if (held !== undefined && held > p) return false
            }
          }
          return true
        }
        const claim = (px: number, py: number) => {
          const x0 = Math.floor((px - pw / 2) / CELL_W)
          const x1 = Math.floor((px + pw / 2) / CELL_W)
          const y0 = Math.floor((py - ph / 2) / CELL_H)
          const y1 = Math.floor((py + ph / 2) / CELL_H)
          for (let cx = x0; cx <= x1; cx++) {
            for (let cy = y0; cy <= y1; cy++) occupied.set(cellKey(cx, cy), p)
          }
        }

        // Place the label by *choosing a spot on its own spoke*, rather than by
        // computing one spot and then dragging it wherever it needs to go. The
        // candidates run outward from the node first (the natural reading), then
        // inward — which is what rescues a node near the left or top rim, whose
        // outward placement would hang off the edge. Every candidate keeps the
        // label on the line between the node and the seed, so it can never stop
        // looking like it belongs to that strand.
        if (isRoot) {
          // The renderer keeps strands out of a field fitted to the ink.
          screen.seedW = ink![0] * ss
          screen.seedH = ink![1] * ss
          screen.seedScale = ss
          // The seed idea sits *in* the seed. It is the one label whose place
          // is not a placement decision — everything else on the canvas is
          // arranged around this point, and moving the words off it breaks the
          // one thing the whole layout is centred on. It was legible on the orb
          // once the caption stopped being a plate and became haloed type.
          lx = x
          ly = y
          // It goes where the seed goes, under the panes and off the stage
          // with the graph it names. It used to be nudged back into the field
          // when the seed left it, which pinned the words to the edge of the
          // thread column while the strands slid on underneath (D350). The
          // panes float above this layer, so the words pass behind them the
          // way the strands do; nothing here has to hide them.
          //
          // The one exception is the run-done column — on a phone, a card the
          // field's width — whose glass the words would read through as if
          // they were set on it. Under it, the caption is hidden, not moved.
          const done = document.querySelector('.rundonecol')
          if (done) {
            const dr = (done as HTMLElement).getBoundingClientRect()
            if (
              dr.height > 0 &&
              lx + pw / 2 > dr.left - hr.left - MAP_PAD &&
              lx - pw / 2 < dr.right - hr.left + MAP_PAD &&
              ly + ph / 2 > dr.top - hr.top - MAP_PAD &&
              ly - ph / 2 < dr.bottom - hr.top + MAP_PAD
            ) {
              hide(el)
              continue
            }
          }
        } else {
          const ux = q.ux
          const uy = q.uy
          let placed = false
          // Node centre to label centre. A single-line caption is ~23px tall,
          // so the first ring has to clear the glyph's own radius.
          //
          // A folded node is the exception: its chevrons occupy the outward
          // spoke out to about 50px, and the space the fold just freed is on
          // the *inward* side — so the caption goes there first, and any
          // outward fallback starts beyond the mark.
          const offsets =
            (folded.get(n.id) ?? 0) > 0
              ? [-22, -40, -58, 86, 106, 126]
              : [20, 38, 56, 76, -20, -38, -56]
          for (const off of offsets) {
            let cx = x + ux * off
            let cy = y + uy * off
            // Never sit on the seed orb.
            const dist = Math.hypot(cx - screen.originX, cy - screen.originY)
            if (dist < ORB && dist > 0.01) {
              cx = screen.originX + ((cx - screen.originX) / dist) * ORB
              cy = screen.originY + ((cy - screen.originY) / dist) * ORB
              // …nor on its own node. The push carries an inward spot back out
              // along the spoke toward the node it names, and since the rim
              // became the seed's clear field (D350) it can carry it all the
              // way: from a rim at its cap it ends within a couple of px of the
              // first ring at the follow's floor, and past it further out. A
              // pushed spot no longer inward of its node by the glyph's
              // clearance is not an inward spot at all, and no inward spot is
              // left on this spoke: the caption takes a later spot or is
              // hidden, never drawn over its dot (D363). An outward spot is
              // only ever pushed further from its node.
              if (off < 0 && (cx - x) * ux + (cy - y) * uy > -GLYPH_CLEAR) continue
            }
            if (!inside(cx, cy) || !free(cx, cy)) continue
            lx = cx
            ly = cy
            placed = true
            break
          }
          if (!placed) {
            hide(el)
            continue
          }
        }
        claim(lx, ly)

        // Deep nodes recede slightly — depth as atmosphere, not as a border.
        //
        // Dead ends are *not* faded further: `opacity` dissolves the halo along
        // with the glyph, so a second recession multiplier took a spent
        // caption below the threshold where it could be read at all. Their ink
        // colour and italic already say what they are, and a well-argued dead
        // end is a result — the one thing it must never be is illegible.
        const rest = isRoot ? 1 : Math.max(0.5, 1 - n.depth * 0.055)
        // Keep every caption readable and keyboard-operable. The canvas marks
        // carry the contextual dim; the dotted rule names the exact target.
        // The one exception is the prior version of a rethought branch: its
        // caption stood level beside the heads that replaced it, two versions
        // stacked with nothing to say which was current, so it recedes with its
        // mark — by less, since a caption must stay legible — unless selected.
        const recede = prior.has(n.id) && n.id !== selectedId ? PRIOR_VERSION_CAPTION : 1
        show(
          el,
          // The seed's camera scale goes LAST in the transform, so it scales the
          // box about its own centre. Folded into the `scale` property instead,
          // it would scale the translation too — the property is applied
          // before `transform` — and the caption would slide toward the
          // stage's corner as the graph zoomed out.
          `translate(-50%,-50%) translate(${lx.toFixed(1)}px,${ly.toFixed(1)}px)${ss === 1 ? '' : ` scale(${ss.toFixed(3)})`}`,
          // `scale` rather than a third `translate`: it is an independent
          // transform property, so it composes with the line above instead of
          // replacing it.
          growth.calm || settle >= 1 ? '1' : (0.93 + 0.07 * settle).toFixed(3),
          (rest * recede * settle).toFixed(3),
        )
      }

      // The keyboard's focus ring, round the node the tree is on. The tree is
      // visually hidden, so this ring IS its focus indicator — placed from the
      // camera that just painted, like every caption, so it stays on the node
      // through the ease the same focus asked for. The seed's ring hugs the orb.
      const ring = ringRef.current
      if (ring) {
        const fid = treeFocus.id
        const q = fid && visible.has(fid) ? screen.place(fid) : null
        if (q && q.on && q.x > -4 && q.x < W + 4 && q.y > -4 && q.y < H + 4) {
          const isRoot = session.nodes[fid!]?.parentId === null
          const d = isRoot ? Math.max(RING_D, screen.orbR * 2 + 12) : RING_D
          ring.style.width = `${d.toFixed(1)}px`
          ring.style.height = `${d.toFixed(1)}px`
          ring.style.transform = `translate(-50%,-50%) translate(${q.x.toFixed(1)}px,${q.y.toFixed(1)}px)`
          ring.style.visibility = 'visible'
        } else {
          ring.style.visibility = 'hidden'
        }
      }
    }

    // Driven by the canvas's loop, not by one of our own — a label has to be
    // placed with the very camera and node positions the strands were just
    // painted with, or it slips off its node whenever the view is moving.
    return onAfterDraw(tick)
  }, [items, showLabels, folded, guideKeep, guideTargetId, visible, session])

  const selectedId = useStore((s) => s.selectedId)

  // What a notch on the canvas means, on the hover of the caption beside it: the
  // card's clause, naming the objections (`OBJECTION_STOOD_BELOW`). The seed's
  // caption is its own element and the seed is not notched.
  const stoodLines = useMemo(() => {
    const out = new Map<string, string>()
    for (const id of stoodAgainst(session)) {
      const names = objectionsStoodUnder(session, id).map((o) => o.label)
      if (names.length) out.set(id, `${stoodClause}: ${names.join(', ')}.`)
    }
    return out
  }, [session])

  return (
    <div className="labels" ref={hostRef}>
      {/* The complete node tree for keyboard and screen-reader users. The visual
          .nlabel buttons below are decluttered with visibility:hidden when they
          would overlap — which drops them from the tab order AND the
          accessibility tree, so an occluded node was reachable only by a sighted
          mouse. The tree is never decluttered: every in-play node, under its
          parent, always reachable, in ONE tab stop. The visual labels are mouse
          affordances only (aria-hidden below), so a node is never announced
          twice. See NodeTree.tsx. */}
      <NodeTree session={session} visible={visible} childrenOf={childrenOf} folded={folded} />
      {/* The tree's focus indicator, drawn on the canvas — placed by the tick. */}
      <div className="nfocus-ring" ref={ringRef} aria-hidden />
      {items.map((n) =>
        n.parentId === null ? (
          <SeedLabel
            key={n.id}
            n={n}
            selected={n.id === selectedId}
            guide={n.id === guideTargetId}
            register={register}
          />
        ) : (
          <Label
            key={n.id}
            n={n}
            fold={folded.get(n.id) ?? 0}
            selected={n.id === selectedId}
            guide={n.id === guideTargetId}
            stood={stoodLines.get(n.id)}
            register={register}
          />
        ),
      )}
    </div>
  )
}
